/**
 * SLAPage.jsx — Acordo de Nível de Serviço (SLA) VUKZ AI
 * Rota pública: /sla
 *
 * Requisito para Microsoft Azure Marketplace (Gate 2):
 *   "Suporte documentado (SLA, canal, tempo de resposta)"
 *
 * Última atualização: 29 de maio de 2026
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Clock, AlertTriangle, Zap, Shield, Mail, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';

const BRAND_GREEN = '#064e3b';
const BRAND_GOLD  = '#b48c2c';

// ─── Small UI helpers ────────────────────────────────────────────────────────

const SectionTitle = ({ children }) => (
  <h2 className="text-2xl font-bold mb-5" style={{ color: BRAND_GREEN }}>
    {children}
  </h2>
);

const Card = ({ children, highlight }) => (
  <div
    className="bg-white rounded-xl border-2 p-6 mb-4"
    style={{ borderColor: highlight ? BRAND_GREEN : '#e5e7eb' }}
  >
    {children}
  </div>
);

const Pill = ({ children, color = 'green' }) => {
  const map = {
    green:  { bg: '#d1fae5', text: '#065f46' },
    gold:   { bg: '#fef3c7', text: '#92400e' },
    red:    { bg: '#fee2e2', text: '#991b1b' },
    gray:   { bg: '#f3f4f6', text: '#374151' },
    blue:   { bg: '#dbeafe', text: '#1e40af' },
  };
  const s = map[color] || map.green;
  return (
    <span className="inline-block px-3 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: s.bg, color: s.text }}>
      {children}
    </span>
  );
};

const Row = ({ label, value, bold }) => (
  <div className="flex items-start justify-between py-2 border-b border-gray-100 last:border-0 gap-4">
    <span className="text-sm text-gray-600 flex-shrink-0">{label}</span>
    <span className={`text-sm text-right ${bold ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>{value}</span>
  </div>
);

const Faq = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl mb-3 overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors"
        onClick={() => setOpen(o => !o)}
      >
        <span className="font-semibold text-sm text-gray-800">{q}</span>
        {open ? <ChevronUp size={16} className="text-gray-400 flex-shrink-0" /> : <ChevronDown size={16} className="text-gray-400 flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-5 pb-4 text-sm text-gray-600 leading-relaxed border-t border-gray-100 bg-gray-50">
          {a}
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

export default function SLAPage() {
  return (
    <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* ── Hero ── */}
      <div style={{ background: `linear-gradient(135deg, ${BRAND_GREEN} 0%, #047857 100%)` }} className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 mb-5">
            <Shield size={14} className="text-green-300" />
            <span className="text-xs font-semibold text-green-200 uppercase tracking-wider">Documentação Oficial</span>
          </div>
          <h1 className="text-4xl font-extrabold text-white mb-4">
            Acordo de Nível de Serviço
          </h1>
          <p className="text-green-100 text-lg mb-2">VUKZ AI — Performance OS para Esportes</p>
          <p className="text-green-200 text-sm">Última atualização: 29 de maio de 2026 · Versão 1.0</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <a href="/termos" className="text-sm text-white/80 hover:text-white underline">Termos de Uso</a>
            <span className="text-white/40">·</span>
            <a href="/privacidade" className="text-sm text-white/80 hover:text-white underline">Política de Privacidade</a>
            <span className="text-white/40">·</span>
            <a href="mailto:suporte@vukz.ai" className="text-sm text-white/80 hover:text-white underline">suporte@vukz.ai</a>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-10 space-y-12">

        {/* ── 1. Objetivo e Âmbito ── */}
        <section>
          <SectionTitle>1. Objetivo e Âmbito</SectionTitle>
          <Card>
            <p className="text-gray-700 leading-relaxed mb-4">
              Este Acordo de Nível de Serviço (<strong>"SLA"</strong>) estabelece os compromissos de disponibilidade,
              desempenho, suporte e manutenção da plataforma <strong>VUKZ</strong>, operada pela{' '}
              <strong>VUKZ AI (Arena Stats, CNPJ 61.698.906/0001-16)</strong>, com sede em Belo Horizonte/MG.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              Este SLA aplica-se a todos os clientes com assinatura ativa (planos Starter Sports, Club Performance
              e Federation/Enterprise, bem como planos do Governance Intelligence OS), adquirida diretamente ou via{' '}
              <strong>Microsoft Azure Marketplace</strong>.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Os termos aqui descritos complementam o contrato de prestação de serviços firmado entre as partes
              (ou os Termos de Uso aceitos na plataforma) e prevalecem sobre comunicações informais.
            </p>
          </Card>
        </section>

        {/* ── 2. Disponibilidade ── */}
        <section>
          <SectionTitle>2. Disponibilidade do Serviço</SectionTitle>

          <Card highlight>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#d1fae5' }}>
                <CheckCircle size={24} style={{ color: BRAND_GREEN }} />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Compromisso de Uptime Mensal</p>
                <p className="text-4xl font-extrabold" style={{ color: BRAND_GREEN }}>99,5%</p>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              O uptime é medido mensalmente excluindo janelas de manutenção programada e eventos fora do controle da VUKZ AI
              (ver Seção 5). O cálculo considera: <code className="bg-gray-100 px-1 rounded">(minutos_disponíveis / minutos_totais_do_mês) × 100</code>.
            </p>
          </Card>

          <Card>
            <h3 className="font-bold text-gray-800 mb-4">Créditos por Descumprimento</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left px-4 py-2.5 font-semibold text-gray-600 rounded-tl-lg">Uptime mensal</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Crédito sobre mensalidade</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-gray-600 rounded-tr-lg">Processo</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-gray-100">
                    <td className="px-4 py-3">≥ 99,5%</td>
                    <td className="px-4 py-3 text-gray-500">Nenhum crédito</td>
                    <td className="px-4 py-3 text-gray-500">—</td>
                  </tr>
                  <tr className="border-t border-gray-100">
                    <td className="px-4 py-3">99,0% – 99,49%</td>
                    <td className="px-4 py-3"><Pill color="gold">10%</Pill></td>
                    <td className="px-4 py-3">Solicitar via ticket</td>
                  </tr>
                  <tr className="border-t border-gray-100">
                    <td className="px-4 py-3">95,0% – 98,99%</td>
                    <td className="px-4 py-3"><Pill color="red">25%</Pill></td>
                    <td className="px-4 py-3">Solicitar via ticket</td>
                  </tr>
                  <tr className="border-t border-gray-100">
                    <td className="px-4 py-3">&lt; 95,0%</td>
                    <td className="px-4 py-3"><Pill color="red">50%</Pill></td>
                    <td className="px-4 py-3">Aplicado automaticamente</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-400 mt-3">
              Créditos são aplicados como desconto no próximo ciclo de faturamento e não são convertíveis em dinheiro.
              O total máximo de créditos em um mês é de 50% da mensalidade daquele período.
            </p>
          </Card>
        </section>

        {/* ── 3. Suporte ── */}
        <section>
          <SectionTitle>3. Canais e Tiers de Suporte</SectionTitle>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* Starter */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 flex flex-col">
              <div className="mb-3">
                <Pill color="gray">Starter Sports</Pill>
              </div>
              <h3 className="font-bold text-gray-900 mb-3">Suporte Básico</h3>
              <ul className="text-sm text-gray-600 space-y-2 flex-1">
                <li className="flex items-start gap-2"><CheckCircle size={14} className="mt-0.5 flex-shrink-0" style={{ color: BRAND_GREEN }} /><span>E-mail: suporte@vukz.ai</span></li>
                <li className="flex items-start gap-2"><CheckCircle size={14} className="mt-0.5 flex-shrink-0" style={{ color: BRAND_GREEN }} /><span>Horário: Seg–Sex, 9h–18h (BRT)</span></li>
                <li className="flex items-start gap-2"><CheckCircle size={14} className="mt-0.5 flex-shrink-0" style={{ color: BRAND_GREEN }} /><span>Tempo de resposta inicial: até 48h úteis</span></li>
                <li className="flex items-start gap-2"><CheckCircle size={14} className="mt-0.5 flex-shrink-0" style={{ color: BRAND_GREEN }} /><span>Base de conhecimento (self-service)</span></li>
              </ul>
            </div>

            {/* Club */}
            <div className="bg-white rounded-xl border-2 p-5 flex flex-col" style={{ borderColor: BRAND_GREEN }}>
              <div className="mb-3">
                <Pill color="green">Club Performance</Pill>
              </div>
              <h3 className="font-bold text-gray-900 mb-3">Suporte Padrão</h3>
              <ul className="text-sm text-gray-600 space-y-2 flex-1">
                <li className="flex items-start gap-2"><CheckCircle size={14} className="mt-0.5 flex-shrink-0" style={{ color: BRAND_GREEN }} /><span>E-mail + Portal de tickets</span></li>
                <li className="flex items-start gap-2"><CheckCircle size={14} className="mt-0.5 flex-shrink-0" style={{ color: BRAND_GREEN }} /><span>Horário: Seg–Sex, 8h–20h (BRT)</span></li>
                <li className="flex items-start gap-2"><CheckCircle size={14} className="mt-0.5 flex-shrink-0" style={{ color: BRAND_GREEN }} /><span>Tempo de resposta: até 24h úteis</span></li>
                <li className="flex items-start gap-2"><CheckCircle size={14} className="mt-0.5 flex-shrink-0" style={{ color: BRAND_GREEN }} /><span>Onboarding guiado (1 sessão)</span></li>
                <li className="flex items-start gap-2"><CheckCircle size={14} className="mt-0.5 flex-shrink-0" style={{ color: BRAND_GREEN }} /><span>Atualizações de incidente via e-mail</span></li>
              </ul>
            </div>

            {/* Federation */}
            <div className="rounded-xl p-5 flex flex-col" style={{ background: `linear-gradient(135deg, ${BRAND_GREEN}10 0%, ${BRAND_GOLD}10 100%)`, border: `2px solid ${BRAND_GOLD}` }}>
              <div className="mb-3">
                <Pill color="gold">Federation / Enterprise</Pill>
              </div>
              <h3 className="font-bold text-gray-900 mb-3">Suporte Premium</h3>
              <ul className="text-sm text-gray-600 space-y-2 flex-1">
                <li className="flex items-start gap-2"><Zap size={14} className="mt-0.5 flex-shrink-0" style={{ color: BRAND_GOLD }} /><span>E-mail, ticket, WhatsApp dedicado</span></li>
                <li className="flex items-start gap-2"><Zap size={14} className="mt-0.5 flex-shrink-0" style={{ color: BRAND_GOLD }} /><span>Horário: 24/7 para incidentes críticos</span></li>
                <li className="flex items-start gap-2"><Zap size={14} className="mt-0.5 flex-shrink-0" style={{ color: BRAND_GOLD }} /><span>Tempo de resposta: até 4h (crítico) / 8h (alto)</span></li>
                <li className="flex items-start gap-2"><Zap size={14} className="mt-0.5 flex-shrink-0" style={{ color: BRAND_GOLD }} /><span>Customer Success Manager dedicado</span></li>
                <li className="flex items-start gap-2"><Zap size={14} className="mt-0.5 flex-shrink-0" style={{ color: BRAND_GOLD }} /><span>Onboarding completo + treinamento</span></li>
                <li className="flex items-start gap-2"><Zap size={14} className="mt-0.5 flex-shrink-0" style={{ color: BRAND_GOLD }} /><span>Revisão técnica trimestral</span></li>
              </ul>
            </div>
          </div>
        </section>

        {/* ── 4. Classificação de Incidentes ── */}
        <section>
          <SectionTitle>4. Classificação de Incidentes e Tempos de Resolução</SectionTitle>
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Severidade</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Descrição</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-gray-600">1ª Resposta</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Resolução alvo</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Planos</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr>
                    <td className="px-4 py-3"><Pill color="red">P1 — Crítico</Pill></td>
                    <td className="px-4 py-3 text-gray-700">Plataforma completamente inacessível ou perda de dados confirmada</td>
                    <td className="px-4 py-3 font-semibold" style={{ color: BRAND_GREEN }}>1h</td>
                    <td className="px-4 py-3">4h</td>
                    <td className="px-4 py-3"><Pill color="gold">Enterprise</Pill></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3"><Pill color="red">P2 — Alto</Pill></td>
                    <td className="px-4 py-3 text-gray-700">Funcionalidade principal degradada; impacto em múltiplos usuários</td>
                    <td className="px-4 py-3 font-semibold" style={{ color: BRAND_GREEN }}>4h</td>
                    <td className="px-4 py-3">24h</td>
                    <td className="px-4 py-3"><Pill color="gold">Club+</Pill></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3"><Pill color="gold">P3 — Médio</Pill></td>
                    <td className="px-4 py-3 text-gray-700">Funcionalidade não crítica afetada; workaround disponível</td>
                    <td className="px-4 py-3 font-semibold" style={{ color: BRAND_GREEN }}>24h</td>
                    <td className="px-4 py-3">72h</td>
                    <td className="px-4 py-3"><Pill color="gray">Todos</Pill></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3"><Pill color="gray">P4 — Baixo</Pill></td>
                    <td className="px-4 py-3 text-gray-700">Questão cosmética, dúvida de uso, melhoria</td>
                    <td className="px-4 py-3 font-semibold" style={{ color: BRAND_GREEN }}>48h</td>
                    <td className="px-4 py-3">Próximo ciclo</td>
                    <td className="px-4 py-3"><Pill color="gray">Todos</Pill></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-400 mt-3">
              Tempos de resposta para planos Starter são contados em horas úteis (Seg–Sex, 9h–18h BRT).
              Planos Enterprise contam em horas corridas para P1 e P2.
            </p>
          </Card>
        </section>

        {/* ── 5. Manutenção Programada ── */}
        <section>
          <SectionTitle>5. Manutenção Programada e Exclusões</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <div className="flex items-center gap-2 mb-3">
                <Clock size={18} style={{ color: BRAND_GREEN }} />
                <h3 className="font-bold text-gray-800">Janelas de Manutenção</h3>
              </div>
              <Row label="Janela padrão" value="Quinzenais, terças-feiras 02h00–04h00 BRT" />
              <Row label="Antecedência mínima" value="48 horas úteis via e-mail" />
              <Row label="Duração máxima" value="2 horas por janela programada" />
              <Row label="Manutenção emergencial" value="Sem aviso prévio; notificação imediata" />
              <p className="text-xs text-gray-400 mt-3">
                Janelas programadas não são contabilizadas no cálculo de uptime.
              </p>
            </Card>
            <Card>
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle size={18} style={{ color: BRAND_GOLD }} />
                <h3 className="font-bold text-gray-800">Exclusões de SLA</h3>
              </div>
              <ul className="text-sm text-gray-600 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0" />
                  <span>Falhas em infraestrutura de terceiros (Azure, provedores de DNS, CDN)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0" />
                  <span>Indisponibilidade causada por uso indevido, scripts abusivos ou DDoS originado pelo cliente</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0" />
                  <span>Força maior: desastres naturais, pandemias, conflitos, interrupções regulatórias</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0" />
                  <span>Períodos de conta suspensa por inadimplência</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0" />
                  <span>Funcionalidades marcadas como "Beta" ou "Preview"</span>
                </li>
              </ul>
            </Card>
          </div>
        </section>

        {/* ── 6. Backup e Recuperação ── */}
        <section>
          <SectionTitle>6. Backup e Recuperação de Dados</SectionTitle>
          <Card>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-800 mb-3">Política de Backup</h3>
                <Row label="Frequência" value="Diária (automática)" />
                <Row label="Retenção" value="30 dias" />
                <Row label="Local" value="Azure SQL — Geo-redundant (Brazil South + US)" />
                <Row label="Criptografia" value="AES-256 em repouso e em trânsito (TLS 1.2+)" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 mb-3">Recuperação de Desastres</h3>
                <Row label="RPO (Recovery Point Objective)" value="Até 24h" />
                <Row label="RTO (Recovery Time Objective)" value="Até 4h (Enterprise) / 8h (demais)" />
                <Row label="Plano de DR" value="Azure Region Failover automatizado" />
                <Row label="Testes de DR" value="Anuais" />
              </div>
            </div>
          </Card>
        </section>

        {/* ── 7. Segurança ── */}
        <section>
          <SectionTitle>7. Segurança e Conformidade</SectionTitle>
          <Card>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { label: 'Infraestrutura', value: 'Microsoft Azure (Brazil South)', icon: '☁️' },
                { label: 'Conformidade', value: 'LGPD (Lei 13.709/2018)', icon: '🛡️' },
                { label: 'Autenticação', value: 'JWT + RBAC por função', icon: '🔐' },
                { label: 'Dados sensíveis', value: 'Criptografados (AES-256)', icon: '🔒' },
                { label: 'Monitoramento', value: '24/7 — Microsoft Purview + alertas', icon: '📊' },
                { label: 'Testes de penetração', value: 'Anuais por terceiros independentes', icon: '🔍' },
                { label: 'Gestão de vulnerabilidades', value: 'Patches críticos em até 72h', icon: '⚡' },
                { label: 'DPO', value: 'Paulo Brandt — privacidade@vukz.ai', icon: '👤' },
              ].map(item => (
                <div key={item.label} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <span className="text-xl">{item.icon}</span>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{item.label}</p>
                    <p className="text-sm text-gray-800 mt-0.5">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </section>

        {/* ── 8. Procedimento para Solicitação de Crédito ── */}
        <section>
          <SectionTitle>8. Como Solicitar Crédito de SLA</SectionTitle>
          <Card>
            <ol className="space-y-4">
              {[
                {
                  n: '1',
                  title: 'Identificar o período de indisponibilidade',
                  desc: 'Registre as datas/horários de início e fim do incidente e o impacto observado na plataforma.',
                },
                {
                  n: '2',
                  title: 'Abrir ticket de solicitação',
                  desc: 'Envie para suporte@vukz.ai com assunto "Solicitação de Crédito SLA — [mês/ano]", incluindo o ID de incidente (se houver) e evidências (screenshots, logs).',
                },
                {
                  n: '3',
                  title: 'Prazo para solicitação',
                  desc: 'Solicitações devem ser feitas em até 30 dias após o término do mês em que o incidente ocorreu.',
                },
                {
                  n: '4',
                  title: 'Análise e aprovação',
                  desc: 'A VUKZ AI analisará a solicitação em até 10 dias úteis, consultando os logs de disponibilidade (Azure Monitor). O cliente será notificado sobre aprovação ou rejeição com justificativa.',
                },
                {
                  n: '5',
                  title: 'Aplicação do crédito',
                  desc: 'Créditos aprovados serão aplicados na próxima fatura como desconto. Não há pagamento em dinheiro.',
                },
              ].map(step => (
                <li key={step.n} className="flex gap-4">
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5"
                    style={{ backgroundColor: BRAND_GREEN }}
                  >
                    {step.n}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">{step.title}</p>
                    <p className="text-sm text-gray-600 mt-0.5">{step.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </Card>
        </section>

        {/* ── 9. Comunicação de Status ── */}
        <section>
          <SectionTitle>9. Status e Comunicação de Incidentes</SectionTitle>
          <Card>
            <Row label="Status page" value="status.vukz.ai (em breve)" bold />
            <Row label="Notificações de incidente" value="E-mail para contatos técnicos cadastrados" />
            <Row label="Atualizações de progresso (P1/P2)" value="A cada 1 hora durante incidente ativo" />
            <Row label="Post-mortem (P1)" value="Relatório em até 5 dias úteis após resolução" />
            <Row label="Comunicados de manutenção" value="E-mail com 48h de antecedência" />
          </Card>
        </section>

        {/* ── FAQ ── */}
        <section>
          <SectionTitle>Perguntas Frequentes</SectionTitle>
          <Faq
            q="O SLA se aplica a funcionalidades em Beta ou Preview?"
            a="Não. Funcionalidades marcadas como 'Beta', 'Preview' ou 'Experimental' na interface estão excluídas do compromisso de uptime e dos créditos de SLA. Utilize essas funcionalidades cientes desse aspecto."
          />
          <Faq
            q="O que acontece se eu adquirir via Azure Marketplace?"
            a="O SLA se aplica da mesma forma independentemente do canal de aquisição. Clientes via Azure Marketplace devem abrir tickets em suporte@vukz.ai. O faturamento de créditos será processado via Microsoft Partner Center conforme as políticas de reembolso do Marketplace."
          />
          <Faq
            q="Posso cancelar o serviço se o SLA for sistematicamente descumprido?"
            a="Sim. Se o uptime ficar abaixo de 95% por dois meses consecutivos, o cliente tem o direito de rescindir o contrato sem multa, mediante notificação por escrito em até 30 dias após o segundo mês afetado."
          />
          <Faq
            q="Como o uptime é medido?"
            a="O uptime é medido por nossa infraestrutura de monitoramento (Azure Monitor + Application Insights), registrando a disponibilidade do endpoint principal da API e da interface web. Os logs são auditáveis e podem ser compartilhados em disputas de crédito."
          />
          <Faq
            q="Existe suporte em inglês?"
            a="Sim. O suporte é prestado primariamente em português (Brasil), mas também atendemos em inglês para clientes internacionais (Enterprise). Para clientes via Azure Marketplace de outras regiões, o suporte em inglês está disponível via e-mail."
          />
        </section>

        {/* ── Contato ── */}
        <section>
          <SectionTitle>Contato e Canais de Suporte</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <a href="mailto:suporte@vukz.ai" className="flex flex-col items-center text-center p-6 bg-white rounded-xl border border-gray-200 hover:border-green-300 hover:shadow-md transition-all group">
              <Mail size={28} className="mb-2 group-hover:scale-110 transition-transform" style={{ color: BRAND_GREEN }} />
              <p className="font-bold text-gray-800 text-sm">Suporte Geral</p>
              <p className="text-xs text-gray-500 mt-1">suporte@vukz.ai</p>
            </a>
            <a href="mailto:privacidade@vukz.ai" className="flex flex-col items-center text-center p-6 bg-white rounded-xl border border-gray-200 hover:border-green-300 hover:shadow-md transition-all group">
              <Shield size={28} className="mb-2 group-hover:scale-110 transition-transform" style={{ color: BRAND_GREEN }} />
              <p className="font-bold text-gray-800 text-sm">Privacidade / DPO</p>
              <p className="text-xs text-gray-500 mt-1">privacidade@vukz.ai</p>
            </a>
            <a href="https://www.vukz.ai" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center text-center p-6 bg-white rounded-xl border border-gray-200 hover:border-green-300 hover:shadow-md transition-all group">
              <ExternalLink size={28} className="mb-2 group-hover:scale-110 transition-transform" style={{ color: BRAND_GREEN }} />
              <p className="font-bold text-gray-800 text-sm">Site Institucional</p>
              <p className="text-xs text-gray-500 mt-1">www.vukz.ai</p>
            </a>
          </div>
        </section>

        {/* ── Footer ── */}
        <div className="pt-8 border-t border-gray-200 text-center text-sm text-gray-500">
          <p>
            VUKZ AI · Arena Stats · CNPJ 61.698.906/0001-16 · Belo Horizonte, MG
          </p>
          <p className="mt-2">
            <Link to="/termos" className="hover:underline mx-2" style={{ color: BRAND_GREEN }}>Termos de Uso</Link>
            ·
            <Link to="/privacidade" className="hover:underline mx-2" style={{ color: BRAND_GREEN }}>Política de Privacidade</Link>
            ·
            <span className="mx-2 font-semibold" style={{ color: BRAND_GREEN }}>SLA (este documento)</span>
          </p>
          <p className="mt-2 text-xs text-gray-400">Versão 1.0 — 29 de maio de 2026</p>
        </div>

      </div>
    </div>
  );
}
